import { quat } from 'gl-matrix';

const RAD_TO_DEG = 180 / Math.PI;

/**
 * Extract Euler angles (in degrees) from a quaternion, matching TRS rotation
 * order: intrinsic XYZ (matrix composed as Rx * Ry * Rz).
 */
export function quatToEulerDegrees(q: quat): [number, number, number] {
  let x = q[0],
    y = q[1],
    z = q[2],
    w = q[3];

  // For intrinsic XYZ (R = Rx * Ry * Rz), the rotation matrix elements are:
  //   R[0][2] = 2(xz + wy) = sin(rotY)
  //   R[1][2] = 2(yz - wx) = -sin(rotX)*cos(rotY)
  //   R[2][2] = 1-2(x²+y²) = cos(rotX)*cos(rotY)
  //   R[0][1] = 2(xy - wz) = -sin(rotZ)*cos(rotY)
  //   R[0][0] = 1-2(y²+z²) = cos(rotZ)*cos(rotY)

  let sinY = 2 * (x * z + w * y);
  let rotY: number;
  if (Math.abs(sinY) >= 1) {
    rotY = Math.sign(sinY) * (Math.PI / 2);
  } else {
    rotY = Math.asin(sinY);
  }

  let rotX = Math.atan2(2 * (w * x - y * z), 1 - 2 * (x * x + y * y));
  let rotZ = Math.atan2(2 * (w * z - x * y), 1 - 2 * (y * y + z * z));

  return [rotX * RAD_TO_DEG, rotY * RAD_TO_DEG, rotZ * RAD_TO_DEG];
}
